import { SelectableWithIdObject } from "src/components/Form/types";
import { toSnakeCase, snakeToTitleCase } from "src/utils/string";

export class EnumBasedItem<E extends string> extends SelectableWithIdObject {
  constructor(
    public id: number,
    public name: string,
    public code: E,
  ) {
    super(id);
  }

  getDisplayName(t?: (key: string) => string): string {
    // Try to use the translation
    if (t) {
      const translated = t(this.name);

      // Check if translation actually happened
      // If the result equals the key, translation is likely missing
      if (translated !== this.name) {
        return translated;
      }
    }

    // Without translation or if translation is missing, format the code to be human-readable
    return snakeToTitleCase(this.code);
  }

  getCode(): string {
    return this.code;
  }

  valueOf(): string {
    return this.code;
  }

  toString(): string {
    return this.code;
  }
}

export class EnumBasedService<T extends EnumBasedItem<E>, E extends string> {
  constructor(protected items: T[]) {}

  getOptions(): T[] {
    return this.items;
  }

  getByName(name: string | undefined): T | undefined {
    if (!name) return undefined;
    return this.items.find((item) => item.name.toLowerCase() === name.toLowerCase());
  }

  getByCode(code: string | undefined): T | undefined {
    if (!code) return undefined;
    return this.items.find((item) => item.code.toLowerCase() === (code || "").toLowerCase());
  }

  getById(id: number | undefined): T | undefined {
    if (!id) return undefined;
    return this.items.find((item) => item.id === id);
  }
}

/**
 * Generates a translation key prefix from a class name
 * @param enumName The name of the class (e.g., "PracticalTraining")
 * @returns Translation key prefix (e.g., "enums.practical_training")
 */
const getEnumNamePrefix = (enumName: string): string => {
  return "enums." + toSnakeCase(enumName);
};

/**
 * Creates an array of item objects from enum values with auto-generated translation keys
 *
 * @param enumObj The enum object containing the values
 * @param enumName The name of the class (used to generate translation keys)
 * @returns Array of objects with id, name (translation key), and code properties
 */
const createEnumBasedItems = <E extends string>(
  enumObj: Record<string, E>,
  enumName: string,
): { id: number; name: string; code: E }[] => {
  const namePrefix = getEnumNamePrefix(enumName);
  return Object.values(enumObj).map((code, index) => ({
    id: index + 1,
    name: `${namePrefix}.${code}`,
    code,
  }));
};

/**
 * Creates an enum-based service with automatic translation key generation
 *
 * This function simplifies the creation of enum-based services by automatically
 * generating translation keys based on the class name and enum values.
 *
 * @example
 * // Define your enum
 * export enum PracticalTrainingType {
 *   FACE_TO_FACE = "face_to_face",
 *   SIMULATION = "simulation",
 *   HELICOPTER = "helicopter",
 * }
 *
 * // Define your item class
 * export class PracticalTraining extends EnumBasedItem<PracticalTrainingType> {}
 *
 * // Create the service
 * const practicalTrainingService = createEnumService(PracticalTrainingType, PracticalTraining);
 *
 * // Translation keys will be automatically generated as:
 * // - "enums.practical_training.face_to_face"
 * // - "enums.practical_training.simulation"
 * // - "enums.practical_training.helicopter"
 *
 * // IMPORTANT: Don't forget to add these keys to your translation files (e.g., en.json):
 * // {
 * //   "enums": {
 * //     "practical_training": {
 * //       "face_to_face": "Face to Face",
 * //       "simulation": "Simulation",
 * //       "helicopter": "Helicopter"
 * //     }
 * //   }
 * // }
 *
 * @param enumType The enum object containing possible values
 * @param itemConstructor The class constructor for items
 * @returns An EnumBasedService instance
 */
export const createEnumService = <T extends EnumBasedItem<E>, E extends string>(
  enumType: Record<string, E>,
  itemConstructor: new (id: number, name: string, code: E) => T,
): EnumBasedService<T, E> => {
  // Create items with auto-generated translation keys based on the class name
  const items = createEnumBasedItems(enumType, itemConstructor.name).map(
    (item) => new itemConstructor(item.id, item.name, item.code),
  );

  return new EnumBasedService<T, E>(items);
};
